export default defineAppConfig({
  ui: {
    // Nuxt UI v4: semantic colors live under ui.colors
    colors: {
      primary: 'blue',
      secondary: 'purple',
      neutral: 'slate',
    },

    // Navigation/PageLinks - fix active state to match old styling
    pageLinks: {
      slots: {
        list: 'flex flex-col gap-0.5',
        link: 'group relative text-sm flex items-center gap-1.5 px-2.5 py-1.5 w-full rounded-md font-normal focus:outline-none focus-visible:outline-none focus-visible:before:ring-inset focus-visible:before:ring-2 focus-visible:before:ring-primary before:absolute before:inset-px before:rounded-md',
        linkLeadingIcon: 'shrink-0 size-5 relative',
        linkLabel: 'truncate relative',
      },
      variants: {
        active: {
          true: {
            link: 'text-gray-900 font-normal dark:text-white before:bg-gray-100 dark:before:bg-gray-800',
            linkLeadingIcon: 'text-gray-900 dark:text-white',
          },
          false: {
            link: [
              'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:before:bg-gray-50 dark:hover:before:bg-gray-800/50',
              'transition-colors',
            ],
            linkLeadingIcon:
              'text-gray-400 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-200',
          },
        },
      },
    },

    // Button - lighter font weight and adjusted sizing
    button: {
      slots: {
        base: 'cursor-pointer rounded-md font-normal inline-flex items-center disabled:cursor-not-allowed aria-disabled:cursor-not-allowed disabled:opacity-75 aria-disabled:opacity-75 transition-colors',
      },
      variants: {
        size: {
          sm: {
            base: 'px-3 py-1.5 text-sm gap-1.5 h-[24px]',
          },
          md: {
            base: 'px-3 py-1.5 text-sm gap-1.5',
            leadingIcon: 'size-4',
            trailingIcon: 'size-4',
          },
        },
        // Make neutral/outline buttons (like ICP, Ad reveal) lighter
        color: {
          neutral: '',
        },
        variant: {
          outline: '',
        },
      },
    },

    // Input - ensure proper sizing
    input: {
      variants: {
        size: {
          sm: {
            base: 'text-sm',
          },
          md: {
            base: 'text-sm',
          },
        },
      },
    },

    // SelectMenu - ensure proper sizing and darker placeholder text
    selectMenu: {
      slots: {
        placeholder: 'truncate text-default',
      },
      variants: {
        size: {
          sm: {
            base: 'text-sm',
          },
          md: {
            base: 'text-sm',
          },
        },
      },
    },

    // Badge - lighter font weight
    badge: {
      slots: {
        base: 'font-normal',
      },
    },

    // Alert - explicit yellow palette for warning color (yellow-100 bg,
    // yellow-700 text) so it reads as higher-emphasis than the defaults.
    alert: {
      compoundVariants: [
        {
          color: 'warning',
          variant: 'soft',
          class: {
            root: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
            description: 'text-yellow-700 dark:text-yellow-300',
          },
        },
        {
          color: 'warning',
          variant: 'subtle',
          class: {
            root: 'bg-yellow-100 text-yellow-700 ring ring-inset ring-yellow-700/30 dark:bg-yellow-900/40 dark:text-yellow-300 dark:ring-yellow-300/30',
            description: 'text-yellow-700 dark:text-yellow-300',
          },
        },
        {
          color: 'warning',
          variant: 'outline',
          class: {
            root: 'text-yellow-700 ring ring-inset ring-yellow-700/30 dark:text-yellow-300 dark:ring-yellow-300/30',
            description: 'text-yellow-700 dark:text-yellow-300',
          },
        },
      ],
    },

    // Card - subtle shadow
    card: {
      slots: {
        root: 'bg-default ring ring-default rounded-lg shadow-sm',
      },
    },

    // Page header styling
    page: {
      header: {
        wrapper: 'border-0',
        title: 'font-header font-black tracking-tight uppercase',
      },
    },

    // Form group spacing
    formGroup: {
      help: 'mt-1',
    },

    // Dashboard components
    dashboard: {
      toolbar: {
        wrapper: 'min-h-[3.5rem]',
      },
    },

    // Dashboard navbar - bolder title
    dashboardNavbar: {
      slots: {
        title: 'flex items-center gap-1.5 font-bold text-lg text-highlighted truncate',
      },
    },

    // Pricing card title
    pricing: {
      card: {
        title: 'font-header font-bold tracking-tight uppercase',
      },
    },

    // Table header styling
    table: {
      th: {
        font: 'font-header font-bold tracking-tight uppercase',
      },
    },

    // Popover styling
    popover: {
      width: 'max-w-[300px]',
      container: 'font-normal text-default text-xs',
      content: 'text-xs',
      base: 'p-3',
    },
    dropdownMenu: {
      slots: {
        content: 'w-(--reka-dropdown-menu-trigger-width)',
      },
    },
    switch: {
      slots: {
        base: 'cursor-pointer',
      },
    },
    modal: {
      slots: {
        content: 'divide-y-0',
        footer: 'items-start',
        // Match the description color to the title (text-highlighted vs default text-muted)
        description: 'text-highlighted',
      },
    },
    // Tabs styling
    tabs: {
      compoundVariants: [
        {
          color: 'neutral',
          variant: 'pill',
          class: {
            indicator: 'bg-white',
            trigger:
              'data-[state=active]:text-gray-700 focus-visible:outline-gray-300 dark:data-[state=active]:text-gray-700',
          },
        },
      ],
    },
  },
});
